import { isAbove, isBelow } from "./comparisons";

// As TV Std.highest / lowest: no value until the window is full

/**
 * @param {(index: number) => number} read
 * @param {number} index - last value of the window
 * @param {number} period
 * @returns {number} the highest of the last `period` values, NaN while the window is not full
 */
export const highestAt = (read, index, period) => {
  if (index - period + 1 < 0) return NaN;

  let highest = -Infinity;

  for (let position = index - period + 1; position <= index; position += 1) {
    highest = Math.max(highest, read(position));
  }

  return highest;
};

/**
 * @param {(index: number) => number} read
 * @param {number} index
 * @param {number} period
 * @returns {number} the lowest of the last `period` values, NaN while the window is not full
 */
export const lowestAt = (read, index, period) => {
  if (index - period + 1 < 0) return NaN;

  let lowest = Infinity;

  for (let position = index - period + 1; position <= index; position += 1) {
    lowest = Math.min(lowest, read(position));
  }

  return lowest;
};

/** Middle of the highest high and the lowest low (Donchian, Ichimoku lines). */
export const channelMiddleAt = (readHigh, readLow, index, period) =>
  (highestAt(readHigh, index, period) + lowestAt(readLow, index, period)) / 2;

// TV Std.highestbars / lowestbars: scans from the newest value back, so the
// newest of equal extremes wins; NaN when the oldest value of the window is missing
const barsSinceExtremeAt = (read, index, period, beats) => {
  if (index - period + 1 < 0 || !Number.isFinite(read(index - period + 1))) return NaN;

  let extreme = read(index);
  let barsAgo = 0;

  for (let distance = 1; distance < period; distance += 1) {
    if (beats(read(index - distance), extreme)) {
      extreme = read(index - distance);
      barsAgo = distance;
    }
  }

  return barsAgo;
};

/** Bars since the highest of the last `period` values (0: the newest one), as TV Std.highestbars (Aroon). */
export const barsSinceHighestAt = (read, index, period) => barsSinceExtremeAt(read, index, period, isAbove);

/** Bars since the lowest of the last `period` values, as TV Std.lowestbars. */
export const barsSinceLowestAt = (read, index, period) => barsSinceExtremeAt(read, index, period, isBelow);
